/**
 * BW Collection Filters — bw-collection-filters.js
 * Handles: desktop dropdowns, mobile sheet, variant image swapping
 */

// Canonical color map for variant image swapping
const CANONICAL_COLOR_MAP = {
  "Black":"Black","Black (With Black Stitching)":"Black","Black (With Grey Stitching)":"Black",
  "Black (With Red Stitching)":"Black","Black (With White Stitching)":"Black",
  "Black (with Blue Stitching)":"Black","Black (with Orange Stitching)":"Black",
  "Black (with White Stitching)":"Black","Black (with Yellow Stitching)":"Black",
  "Panther Black":"Black","Deep Black":"Black","Black Leather / Silver Butterfly Clasp":"Black",
  "Black & White":"Black","Black & Red":"Black","Black Leather & Yellow Underside":"Black",
  "Brown":"Brown","Dark Brown":"Brown","Monaco Brown":"Brown","Riviera Brown":"Brown",
  "Brown Leather / Silver Butterfly Clasp":"Brown","Dark Brown Leather & Blue Underside":"Brown",
  "Light Brown":"Brown","Champagne":"White","Champagne Leather / Silver Butterfly Clasp":"White","Beige":"White",
  "Navy":"Navy / Blue","Navy Blue":"Navy / Blue","Navy (with Blue Stitching)":"Navy / Blue",
  "Navy (with White stitching)":"Navy / Blue","Navy Leather & White Underside":"Navy / Blue",
  "Midnight Blue":"Navy / Blue","Dark Blue":"Navy / Blue","Slate Blue":"Navy / Blue",
  "Blue":"Navy / Blue","Sky Blue":"Navy / Blue","Light Blue":"Navy / Blue","Sea Blue":"Navy / Blue",
  "Tropical Blue":"Navy / Blue","Turquoise Blue":"Navy / Blue","Regatta Blue":"Navy / Blue","Royal Blue & White":"Navy / Blue",
  "British Racing Green":"Green","Army Green":"Green","Dark Green":"Green","Forrest Green":"Green",
  "Green":"Green","Ivy Green":"Green","Lime Green":"Green","Olive Green":"Green",
  "Green & Grey":"Green","Green ( with White Stitching)":"Green","Green (with Green Stitching)":"Green",
  "Orange":"Orange","Blood Orange & Black":"Orange","Deep Orange":"Orange","Summer Orange":"Orange","Vibrant Orange":"Orange",
  "Red":"Red","Bright Red":"Red","Dark Red":"Red","Deep Red":"Red","Wine Red":"Red","Red & Black":"Red",
  "Gray":"Grey","Gray (with White Stitching)":"Grey","Grey":"Grey","Slate Grey":"Grey","Grey & Black":"Grey",
  "Candy White":"White","Satin White":"White","Off White":"White","Oyster":"White",
  "Cream (with Cream Stitching)":"White","Cream (with White Stitching)":"White",
  "Starlight":"White","White Leather / Silver Butterfly Clasp":"White","Magnolia":"White",
  "Yellow":"Yellow","Sand Yellow":"Yellow","Passion Yellow":"Yellow","Yellow & Black":"Yellow",
  "Pink":"Pink","Dark Pink":"Pink","Rose Pink":"Pink","Misty Rose":"Pink","Pink Leather / Silver Butterfly Clasp":"Pink",
  "Silver":"Silver","Dark Purple":"Purple"
};

// ——— Black-buckle page: lock images immediately at DOMContentLoaded ———
// Liquid already rendered the correct black variant image server-side.
// Lock it right away so Swatch King (or any other app) can't override it.
const isBlackBucklePage = /\/collections\/black-buckle(\/|$|\?)/.test(window.location.pathname);

function lockBlackBuckleImages() {
  if (!isBlackBucklePage) return;
  document.querySelectorAll('.bw-card-product').forEach(card => {
    const img = card.querySelector('.bw-card-image');
    if (!img || !img.src) return;
    const targetSrc = img.src;
    img.dataset.bwLockedSrc = targetSrc;
    if (!img._bwObserver) {
      img._bwObserver = new MutationObserver(() => {
        if (img.dataset.bwLockedSrc && img.src !== img.dataset.bwLockedSrc) {
          img.src = img.dataset.bwLockedSrc;
          img.srcset = img.dataset.bwLockedSrc;
        }
      });
      img._bwObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
    }
  });
}

// ——— Buckle variant image swapping ———
// Runs AFTER applyColorFilter so it can respect the colour already locked in.
function applyBuckleFilter() {
  const params = new URLSearchParams(window.location.search);

  // Find the active buckle value from any URL param whose key contains "buckle"
  let activeBuckle = null;
  for (const [key, value] of params.entries()) {
    if (key.toLowerCase().includes('buckle')) {
      activeBuckle = value;
      break;
    }
  }
  if (!activeBuckle && isBlackBucklePage) activeBuckle = 'Black';
  if (!activeBuckle) return;

  document.querySelectorAll('.bw-card-product').forEach(card => {
    try {
      // Use 'null' fallback so JSON.parse doesn't throw on empty string
      const buckleVariants = JSON.parse(card.dataset.bwBuckleVariants || 'null') || [];
      let match = null;

      if (buckleVariants && buckleVariants.length) {
        // Products with an explicit "Buckle & Adapters" option
        const lockedVariantId = card.dataset.bwVariantId;
        if (lockedVariantId) {
          const colorVariants = JSON.parse(card.dataset.colorVariants || '[]');
          const lockedColorVariant = colorVariants.find(v => String(v.variant_id) === String(lockedVariantId));
          if (lockedColorVariant) {
            const canonical = CANONICAL_COLOR_MAP[lockedColorVariant.raw] || lockedColorVariant.raw;
            match = buckleVariants.find(v =>
              (CANONICAL_COLOR_MAP[v.color] || v.color) === canonical && v.buckle === activeBuckle
            );
          }
        }
        if (!match) match = buckleVariants.find(v => v.buckle === activeBuckle);
      } else if (isBlackBucklePage && activeBuckle === 'Black') {
        // Fallback for products without a buckle option (e.g. The Presidential):
        // they come with black hardware by default when the colour is Black —
        // so just show their Black colour variant image.
        const colorVariants = JSON.parse(card.dataset.colorVariants || '[]');
        const colorMatch = colorVariants.find(v => (CANONICAL_COLOR_MAP[v.raw] || v.raw) === 'Black');
        if (colorMatch) match = { image: colorMatch.image, variant_id: colorMatch.variant_id };
      }

      if (!match) return;

      // Swap card image
      const img = card.querySelector('.bw-card-image');
      if (img && match.image) {
        const targetSrc = match.image.startsWith('//') ? 'https:' + match.image : match.image;
        img.src = targetSrc;
        img.srcset = targetSrc;
        img.dataset.bwLockedSrc = targetSrc;
        // Lock against reversion by Swatch King or other scripts
        if (!img._bwObserver) {
          img._bwObserver = new MutationObserver(() => {
            if (img.dataset.bwLockedSrc && img.src !== img.dataset.bwLockedSrc) {
              img.src = img.dataset.bwLockedSrc;
              img.srcset = img.dataset.bwLockedSrc;
            }
          });
          img._bwObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
        }
      }

      // Update product links to deep-link to the matched variant
      if (match.variant_id) {
        card.querySelectorAll('a[href]').forEach(link => {
          try {
            const url = new URL(link.href);
            if (url.pathname.includes('/products/')) {
              url.searchParams.set('variant', match.variant_id);
              link.href = url.toString();
            }
          } catch(e) {}
        });
        // Capture-phase click guard (beats Swatch King's bubble-phase handlers)
        if (!card._bwClickGuarded) {
          card._bwClickGuarded = true;
          card.addEventListener('click', (e) => {
            const varId = card.dataset.bwVariantId;
            if (!varId) return;
            const clickedLink = e.target.closest('a[href*="/products/"]');
            if (clickedLink) {
              e.preventDefault();
              e.stopImmediatePropagation();
              const url = new URL(clickedLink.href);
              url.searchParams.set('variant', varId);
              window.location.href = url.toString();
            }
          }, true);
        }
        card.dataset.bwVariantId = match.variant_id;
      }
    } catch(e) {}
  });
}

// ——— Colour variant image swapping ———
function applyColorFilter() {
  const params = new URLSearchParams(window.location.search);
  const activeColors = params.getAll('filter.p.m.custom.filter_color');
  if (!activeColors.length) return;

  document.querySelectorAll('.bw-card-product').forEach(card => {
    try {
      const variants = JSON.parse(card.dataset.colorVariants || '[]');
      if (!variants.length) return;

      // Find first variant matching any active color
      let match = null;
      for (const activeColor of activeColors) {
        match = variants.find(v => CANONICAL_COLOR_MAP[v.raw] === activeColor);
        if (match) break;
      }
      if (!match) return;

      // Swap image + lock it against reversion by other scripts (Swatch King etc.)
      const img = card.querySelector('.bw-card-image');
      if (img && match.image) {
        const targetSrc = match.image.startsWith('//') ? 'https:' + match.image : match.image;
        img.src = targetSrc;
        img.srcset = targetSrc;
        img.dataset.bwLockedSrc = targetSrc;
        // If any other script changes the src, immediately restore it
        if (!img._bwObserver) {
          img._bwObserver = new MutationObserver(() => {
            if (img.dataset.bwLockedSrc && img.src !== img.dataset.bwLockedSrc) {
              img.src = img.dataset.bwLockedSrc;
              img.srcset = img.dataset.bwLockedSrc;
            }
          });
          img._bwObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
        }
      }
      // Intercept ALL product link clicks at capture phase — runs before Swatch King
      if (match.variant_id) {
        // Also update hrefs as a baseline
        card.querySelectorAll('a[href]').forEach(link => {
          try {
            const url = new URL(link.href);
            if (url.pathname.includes('/products/')) {
              url.searchParams.set('variant', match.variant_id);
              link.href = url.toString();
            }
          } catch(e) {}
        });
        // Capture-phase click interceptor beats Swatch King's bubble-phase handler
        if (!card._bwClickGuarded) {
          card._bwClickGuarded = true;
          card.addEventListener('click', (e) => {
            const varId = card.dataset.bwVariantId;
            if (!varId) return;
            const clickedLink = e.target.closest('a[href*="/products/"]');
            if (clickedLink) {
              e.preventDefault();
              e.stopImmediatePropagation();
              const url = new URL(clickedLink.href);
              url.searchParams.set('variant', varId);
              window.location.href = url.toString();
            }
          }, true); // capture = true: fires before any bubble-phase handlers
        }
        card.dataset.bwVariantId = match.variant_id;
      }
    } catch(e) {}
  });
}

// ——— Desktop dropdown logic ———
function initDesktopDropdowns() {
  const pills = document.querySelectorAll('.bw-filter-pill[data-filter-index]');
  const dropdowns = document.querySelectorAll('.bw-filter-dropdown');

  function closeAll() {
    dropdowns.forEach(d => {
      d.classList.remove('open');
      d.setAttribute('aria-hidden', 'true');
    });
    pills.forEach(p => {
      p.classList.remove('open');
      p.setAttribute('aria-expanded', 'false');
    });
  }

  pills.forEach(pill => {
    const idx = pill.dataset.filterIndex;
    const dropdown = document.querySelector(`.bw-filter-dropdown[data-filter-index="${idx}"]`);
    if (!dropdown) return;

    pill.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.classList.contains('open');
      closeAll();
      if (!isOpen) {
        dropdown.classList.add('open');
        dropdown.setAttribute('aria-hidden', 'false');
        pill.classList.add('open');
        pill.setAttribute('aria-expanded', 'true');
        // Position dropdown below pill
        const rect = pill.getBoundingClientRect();
        dropdown.style.top = (rect.bottom + window.scrollY + 8) + 'px';
        dropdown.style.left = rect.left + 'px';
      }
    });
  });

  document.addEventListener('click', closeAll);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeAll();
  });

  // Desktop: submit the single filter form on any checkbox change (full page reload)
  const filterForm = document.getElementById('FacetFiltersForm');
  if (filterForm) {
    filterForm.querySelectorAll('input[type=checkbox]').forEach(cb => {
      cb.addEventListener('change', () => filterForm.submit());
    });
  }

  // Sort: auto-submit on change
  const sortSelect = document.getElementById('BwSortBy');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      sortSelect.closest('form')?.submit();
    });
  }
}

// ——— Mobile sheet logic ———
function initMobileSheet() {
  const trigger = document.querySelector('.bw-filter-pill--mobile-trigger');
  const sheet = document.querySelector('.bw-filter-sheet');
  if (!trigger || !sheet) return;

  const closeBtn = sheet.querySelector('.bw-filter-sheet__close');
  const clearBtn = sheet.querySelector('.bw-filter-sheet__clear');
  const showBtn = sheet.querySelector('.bw-filter-sheet__show');
  const sheetForm = sheet.querySelector('form');

  trigger.addEventListener('click', () => {
    sheet.classList.add('open');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  });

  function closeSheet() {
    sheet.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  closeBtn?.addEventListener('click', closeSheet);

  clearBtn?.addEventListener('click', () => {
    sheet.querySelectorAll('input[type=checkbox]').forEach(cb => cb.checked = false);
    updateSheetCount();
  });

  showBtn?.addEventListener('click', () => {
    sheetForm?.submit();
  });

  // Accordion
  sheet.querySelectorAll('.bw-sheet-accordion__trigger').forEach(accTrigger => {
    accTrigger.addEventListener('click', () => {
      const acc = accTrigger.closest('.bw-sheet-accordion');
      const isOpen = acc.classList.contains('open');
      acc.classList.toggle('open');
      accTrigger.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });

  // Live count update
  function updateSheetCount() {
    const checked = sheet.querySelectorAll('input[type=checkbox]:checked').length;
    if (showBtn) {
      showBtn.textContent = checked > 0 ? `Show results (${checked} selected)` : 'Show results';
    }
    const subheader = sheet.querySelector('.bw-filter-sheet__subheader');
    if (subheader) {
      subheader.style.display = checked > 0 ? 'block' : 'none';
      const countEl = subheader.querySelector('.bw-selected-count');
      if (countEl) countEl.textContent = checked;
    }
    // Update mobile trigger pill count badge
    const badge = trigger.querySelector('.bw-filter-pill__count');
    if (badge) badge.textContent = checked;
    if (checked > 0) {
      trigger.classList.add('active');
    } else {
      trigger.classList.remove('active');
    }
  }

  sheet.querySelectorAll('input[type=checkbox]').forEach(cb => {
    cb.addEventListener('change', updateSheetCount);
  });
  updateSheetCount();
}

function bwFiltersInit() {
  initDesktopDropdowns();
  initMobileSheet();
}

// Apply colour + buckle filters AFTER all other scripts have initialised (window.load)
// plus a safety delay to beat any lazy variant pickers / Swatch King reinits.
// Buckle runs after colour so it can respect the colour already locked in.
function bwApplyColorFilterLate() {
  applyColorFilter();
  applyBuckleFilter();
  // Re-apply after a short delay in case other scripts reset images post-load
  setTimeout(() => { applyColorFilter(); applyBuckleFilter(); }, 300);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { bwFiltersInit(); lockBlackBuckleImages(); });
} else {
  bwFiltersInit();
  lockBlackBuckleImages();
}

// Color swap must run after everything else
window.addEventListener('load', bwApplyColorFilterLate);
